import RecruitChip from './RecruitChip';
import PmBlock from './PmBlock';
import { useState, useEffect } from 'react';
import { Card, SectionTitle, Btn, Modal, Input, SaveBanner } from './UI';
import StatusPicker, { getStatusColor, statusShort } from './StatusPicker';
import VacationCalendar from './VacationCalendar';

const DOW_RU = ['вс','пн','вт','ср','чт','пт','сб'];
const WEEK_COLORS = {
  36: { hdr: '#2E75B6', light: '#D6E4F0' },
  37: { hdr: '#70AD47', light: '#E2EFDA' },
  38: { hdr: '#FFC000', light: '#FFF2CC' },
  39: { hdr: '#F4B183', light: '#FCE4D6' },
  40: { hdr: '#808080', light: '#EDEDED' },
};
const OVERLAP = new Set(['БЛ+МР','БЛ+ПМ','БЛ+ВЫХ','ОТ+МР','ОТ+ПМ','ОТ+ВЫХ','ДО+МР','ДО+ПМ','ДО+ВЫХ','ВК+МР','ВК+ПМ','ВК+ВЫХ']);

function getMrc(route) {
  const m = route?.merchandisers;
  if (!m) return null;
  return Array.isArray(m) ? m[0] : m;
}

function isWeekend(dow) { return dow === 0 || dow === 6; }
function getMonthAbbr(month) { return ['','янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'][month]; }

function calcMetrics(statuses, routeId, weekDays) {
  let exits=0, sick=0, vacation=0, dayoff=0, vacancy=0, overlaps=0;
  weekDays.forEach(({ date }) => {
    const s = statuses[routeId]?.[date];
    if (!s) return;
    if (s==='1'||s==='СВ') exits++;
    if (s==='БЛ-') sick++;
    if (s==='ОТ-') vacation++;
    if (s==='ДО-') dayoff++;
    if (s==='ВК-') vacancy++;
    if (OVERLAP.has(s)) overlaps++;
  });
  return { exits, sick, vacation, dayoff, vacancy, overlaps };
}

function getVacationConflicts(routes, vacations) {
  const conflicts = [];
  const vacFor = (id) => vacations.find(v => v.route_id === id && v.status === 'approved');
  const withVac = routes.filter(r => !r.is_vacancy && vacFor(r.id));
  for (let i = 0; i < withVac.length; i++) {
    for (let j = i+1; j < withVac.length; j++) {
      const a = vacFor(withVac[i].id), b = vacFor(withVac[j].id);
      if (a.date_from <= b.date_to && b.date_from <= a.date_to) {
        const ov_from = a.date_from > b.date_from ? a.date_from : b.date_from;
        const ov_to   = a.date_to   < b.date_to   ? a.date_to   : b.date_to;
        conflicts.push({ mrc1: getMrc(withVac[i])?.full_name, mrc2: getMrc(withVac[j])?.full_name, from: ov_from, to: ov_to });
      }
    }
  }
  return conflicts;
}

/**
 * Полностью редактируемый табель одного СВ.
 * Используется и на странице самого СВ, и внутри карточки ТМ (drill-down).
 *
 * Props:
 *  - period, weeks, weekNums
 *  - routes, statuses, vacations, servicePct — данные (управляются снаружи через onDataChange)
 *  - onSetStatus({routeId, date, code}) — async
 *  - onSetVacation({routeId, dateFrom, dateTo, workingDays}) — async
 *  - onDeleteVacation(routeId) — async
 *  - onCloseVacancy(routeId, fullName) — async
 *  - editable — true/false (на будущее, сейчас всегда true когда рендерится)
 */
export default function EditableTabell({
  period, weeks, weekNums, routes, statuses, vacations, servicePct = {}, visitFacts = [],
  onSetStatus, onSetVacation, onDeleteVacation, onCloseVacancy,
  onLocalStatusChange, onLocalVacationChange, onLocalRouteChange,
  onApprove, onReject, isTmAction = false, focus = null, onHideRoute = null, pmRoutes = [],
}) {
  const [subTab, setSubTab] = useState(focus?.tab === 'vac' ? 1 : 0);
  const [flash, setFlash] = useState(null); // подсветка после перехода из алёрта
  const [picker, setPicker] = useState(null);
  const [vacancyFill, setVacancyFill] = useState(null);
  const [vacancyName, setVacancyName] = useState('');
  const [vacancySaving, setVacancySaving] = useState(false);
  const [banner, setBanner] = useState({ show: false, msg: '' });

  // Переход из алёрта: открыть нужную вкладку, прокрутить к маршруту/отпуску/дню и подсветить
  useEffect(() => {
    if (!focus) return;
    setSubTab(focus.tab === 'vac' ? 1 : 0);
    setFlash(focus);
    const t1 = setTimeout(() => {
      const el = document.getElementById(
        focus.tab === 'vac' && focus.routeId ? `vacrow-${focus.routeId}`
        : focus.routeId && focus.date ? `tabday-${focus.routeId}-${focus.date}`
        : focus.routeId ? `tabrow-${focus.routeId}` : '');
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    const t2 = setTimeout(() => setFlash(null), 6000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [focus?.key]);

  const flashRows = new Set(flash && flash.tab !== 'vac' ? (flash.routeIds || (flash.routeId ? [flash.routeId] : [])) : []);

  function showBanner(msg) {
    setBanner({ show: true, msg });
    setTimeout(() => setBanner({ show: false, msg: '' }), 2000);
  }

  async function handleSetStatus(code) {
    if (!picker) return;
    const { routeId, date } = picker;
    try {
      await onSetStatus({ routeId, date, code });
      onLocalStatusChange?.(routeId, date, code);
      showBanner(code ? '✓ Сохранено' : '✕ Статус удалён');
    } catch (err) {
      showBanner('⚠ Ошибка: ' + err.message);
    }
  }

  async function handleFillVacancy() {
    if (!vacancyFill || !vacancyName.trim()) return;
    setVacancySaving(true);
    try {
      await onCloseVacancy(vacancyFill.routeId, vacancyName.trim());
      onLocalRouteChange?.(vacancyFill.routeId, { is_vacancy: false, merchandisers: { full_name: vacancyName.trim() } });
      setVacancyFill(null);
      setVacancyName('');
      showBanner('✓ Вакансия заполнена');
    } catch (err) {
      showBanner('⚠ Ошибка: ' + err.message);
    } finally {
      setVacancySaving(false);
    }
  }

  const conflicts = getVacationConflicts(routes, vacations);

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, borderBottom: '1px solid var(--border)' }}>
        {['Табель', 'Отпуска'].map((t, i) => (
          <button key={i} onClick={() => setSubTab(i)} style={{
            padding: '8px 14px', fontSize: 12, fontWeight: subTab===i?600:500,
            color: subTab===i ? 'var(--accent)' : 'var(--text-muted)',
            background: 'none', border: 'none',
            borderBottom: `2px solid ${subTab===i ? 'var(--accent)' : 'transparent'}`,
          }}>{t}</button>
        ))}
      </div>

      {subTab === 0 && (
        <>
          {conflicts.length > 0 && (
            <div onClick={() => setSubTab(1)} style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: 'var(--red)', fontWeight: 600, cursor: 'pointer' }}>
              ⚠️ Пересечение отпусков — {conflicts.length}. Нажмите чтобы посмотреть →
            </div>
          )}

          <Card>
            {routes.map(route => {
              const mrc = getMrc(route);
              const mrcName = mrc?.full_name || 'Вакансия';
              const isVacant = route.is_vacancy || !mrc?.full_name;
              const hasConflict = conflicts.some(c => c.mrc1 === mrcName || c.mrc2 === mrcName);

              return (
                <div key={route.id} id={`tabrow-${route.id}`} style={{ borderBottom: '1px solid var(--border)', padding: '12px 16px', background: flashRows.has(route.id) ? '#FFF8DC' : 'transparent', transition: 'background .4s' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: isVacant ? 'var(--text-muted)' : 'var(--text)', fontStyle: isVacant ? 'italic' : 'normal', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {mrcName}
                    {hasConflict && <span style={{ fontSize: 12 }}>⚠️</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>{route.internal_code}
                    <RecruitChip routeId={route.id} style={{ marginLeft: 8, verticalAlign: 'middle' }} />
                    {onHideRoute && (
                      <button onClick={() => {
                        if (window.confirm(`Скрыть маршрут ${route.internal_code} (${mrcName})?\nЗаказ не удаляется, маршрут пропадёт из табеля и расчётов. Вернуть можно в «Скрытые маршруты».`)) {
                          onHideRoute(route).then(() => showBanner('✓ Маршрут скрыт')).catch(e => showBanner('⚠ ' + e.message));
                        }
                      }} style={{ marginLeft: 10, fontSize: 11, color: '#9C0006', background: 'none', border: '1px dashed #E0A0A0', borderRadius: 6, padding: '1px 8px', cursor: 'pointer' }}>Скрыть маршрут</button>
                    )}
                  </div>
                  <button onClick={() => { setVacancyFill({ routeId: route.id, code: route.internal_code }); setVacancyName(isVacant ? '' : mrcName); }}
                    style={{ display: 'inline-flex', gap: 6, padding: '5px 10px', borderRadius: 6, border: `1.5px dashed ${isVacant ? 'var(--accent)' : 'var(--border)'}`, background: isVacant ? 'var(--accent-light)' : 'var(--surface2)', color: isVacant ? 'var(--accent)' : 'var(--text-muted)', fontSize: 11, fontWeight: 600, cursor: 'pointer', marginBottom: 8 }}>
                    {isVacant ? '👤 Заполнить вакансию' : '✏️ Сменить МРЧ'}
                  </button>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowX: 'auto' }}>
                    {weekNums.map(w => {
                      const wc = WEEK_COLORS[w] || WEEK_COLORS[36];
                      const wDays = weeks[w]?.days || [];
                      const lead = wDays.length ? (wDays[0].dow + 6) % 7 : 0; // пустые ячейки до первого дня, чтобы Пн–Вс шли столбцами
                      return (
                        <div key={w} style={{ display: 'flex', gap: 3, alignItems: 'flex-end', flexWrap: 'nowrap' }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: 'white', background: wc.hdr, padding: '1px 5px', borderRadius: 3, alignSelf: 'flex-end', marginBottom: 2, width: 26, textAlign: 'center', boxSizing: 'content-box', flex: 'none' }}>W{w}</div>
                          {Array.from({ length: lead }).map((_, i) => <div key={`pad${i}`} style={{ width: 30, flex: 'none' }} />)}
                          {wDays.map(({ day, dow, date }) => {
                            const s = statuses[route.id]?.[date];
                            const sc = s ? getStatusColor(s) : null;
                            const isWe = isWeekend(dow);
                            const factForDay = (visitFacts || []).find(vf => vf.route_id === route.id && vf.work_date === date);
                            return (
                              <div key={date} id={`tabday-${route.id}-${date}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, borderRadius: 6, outline: (flash && flash.date === date && flashRows.has(route.id)) ? '2px solid #E0A800' : 'none', outlineOffset: 1 }}>
                                <span style={{ fontSize: 8, color: 'var(--text-light)' }}>{DOW_RU[dow].slice(0,2).toUpperCase()}</span>
                                <div onClick={() => setPicker({ routeId: route.id, date, mrcName: `${mrcName} · ${day} ${getMonthAbbr(period?.month || 9)}` })}
                                  style={{
                                    width: 30, height: 30, borderRadius: 5,
                                    border: `1.5px solid ${sc ? 'transparent' : 'var(--border)'}`,
                                    background: sc ? sc.bg : isWe ? '#F7F7F7' : 'white',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 9, fontWeight: 700, color: sc ? sc.fg : 'var(--text-muted)', cursor: 'pointer',
                                  }}>
                                  {s ? statusShort(s) : ''}
                                </div>
                                <span style={{ fontSize: 8, color: 'var(--text-light)' }}>{day}</span>
                                {factForDay?.service_pct != null && (
                                  <span style={{ fontSize: 7, color: '#2E75B6', fontWeight: 700 }}>{(factForDay.service_pct*100).toFixed(0)}%</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </Card>
          <PmBlock routes={pmRoutes} />
        </>
      )}

      {subTab === 1 && (
        <VacationEditor
          period={period}
          routes={[...routes, ...pmRoutes]} vacations={vacations}
          onSetVacation={onSetVacation} onDeleteVacation={onDeleteVacation}
          onLocalVacationChange={onLocalVacationChange}
          showBanner={showBanner}
          onApprove={onApprove} onReject={onReject}
          flashRoute={flash && flash.tab === 'vac' ? flash.routeId : null}
        />
      )}

      <StatusPicker open={!!picker} onClose={() => setPicker(null)} onSelect={handleSetStatus} date={picker?.mrcName} />

      <Modal open={!!vacancyFill} onClose={() => { setVacancyFill(null); setVacancyName(''); }} title={`ФИО МРЧ · ${vacancyFill?.code || ''}`}>
        <div style={{ padding: '0 20px 20px' }}>
          <Input label="ФИО нового МРЧ" value={vacancyName} onChange={setVacancyName} placeholder="Иванова Мария Петровна" />
          <Btn size="full" onClick={handleFillVacancy} disabled={!vacancyName.trim() || vacancySaving}>
            {vacancySaving ? 'Сохранение...' : 'Сохранить'}
          </Btn>
        </div>
      </Modal>

      <SaveBanner show={banner.show} message={banner.msg} />
    </div>
  );
}

function VacationEditor({ period, routes, vacations, onSetVacation, onDeleteVacation, onLocalVacationChange, showBanner, onApprove, onReject, flashRoute = null }) {
  const [pickerOpen, setPickerOpen] = useState(null);
  const [selecting, setSelecting] = useState(null);
  const [saving, setSaving] = useState(false);
  // месяц берём из периода (раньше был зашит сентябрь)
  const pYear = period?.year || +String(period?.date_from || '2026-09').slice(0, 4);
  const pMonth = period?.month || +String(period?.date_from || '2026-09').slice(5, 7);
  const MM = String(pMonth).padStart(2, '0');
  const DIM = new Date(pYear, pMonth, 0).getDate();
  const MON = getMonthAbbr(pMonth);
  const OFFSET = (new Date(pYear, pMonth - 1, 1).getDay() + 6) % 7; // 0 = месяц начинается с понедельника
  const DOW_HDRS = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

  function vacFor(routeId) { return vacations.find(v => v.route_id === routeId); }
  function isWeekendDay(day) { const dow = (OFFSET + day - 1) % 7; return dow === 5 || dow === 6; }
  function workDays(from, to) { let c = 0; for (let d = from; d <= to; d++) if (!isWeekendDay(d)) c++; return c; }

  function openPicker(routeId) {
    const v = vacFor(routeId);
    setSelecting(v ? { from: parseInt(v.date_from.split('-')[2]), to: parseInt(v.date_to.split('-')[2]) } : { from: null, to: null });
    setPickerOpen(routeId);
  }

  function dayClick(day) {
    if (isWeekendDay(day)) return;
    setSelecting(prev => {
      if (!prev.from || (prev.from && prev.to)) return { from: day, to: null };
      const [f, t] = prev.from < day ? [prev.from, day] : [day, prev.from];
      return { from: f, to: t };
    });
  }

  async function save() {
    if (!selecting?.from || !selecting?.to || !pickerOpen) return;
    setSaving(true);
    try {
      const toISO = d => `${pYear}-${MM}-${String(d).padStart(2,'0')}`;
      const wd = workDays(selecting.from, selecting.to);
      await onSetVacation({ routeId: pickerOpen, dateFrom: toISO(selecting.from), dateTo: toISO(selecting.to), workingDays: wd });
      onLocalVacationChange?.(pickerOpen, { date_from: toISO(selecting.from), date_to: toISO(selecting.to), working_days: wd });
      setPickerOpen(null);
      showBanner('📅 Отпуск сохранён');
    } catch (err) {
      showBanner('⚠ Ошибка: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function remove(routeId) {
    try {
      await onDeleteVacation(routeId);
      onLocalVacationChange?.(routeId, null);
      showBanner('✕ Отпуск удалён');
    } catch (err) { showBanner('⚠ Ошибка'); }
  }

  return (
    <>
    <SectionTitle>Календарь пересечений</SectionTitle>
    <Card style={{ marginBottom: 12 }}>
      <div style={{ padding: 12 }}>
        <VacationCalendar
          routes={routes}
          vacations={vacations}
          monthPrefix={period?.date_from?.slice(0, 7)}
          daysInMonth={period ? new Date(period.year, period.month, 0).getDate() : 30}
          offset={period ? new Date(period.year, period.month - 1, 1).getDay() : 1}
          onOpenRoute={(routeId) => {
            document.getElementById(`vacrow-${routeId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            openPicker(routeId);
          }}
        />
      </div>
    </Card>

    <Card>
      {routes.filter(r => !r.is_vacancy).map(route => {
        const mrc = getMrc(route);
        const v = vacFor(route.id);
        const isOpen = pickerOpen === route.id;
        return (
          <div key={route.id} id={`vacrow-${route.id}`} style={{ borderBottom: '1px solid var(--border)', padding: '12px 16px', background: flashRoute === route.id ? '#FFF8DC' : 'transparent', transition: 'background .4s' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{mrc?.full_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{route.internal_code}</div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {v && (
                  <>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20,
                      background: v.status === 'approved' ? '#FFF9C4' : v.status === 'rejected' ? '#F0F0F0' : '#E5E5E5',
                      color: v.status === 'approved' ? '#7D5A00' : v.status === 'rejected' ? '#9C0006' : '#666',
                      border: `1px solid ${v.status === 'approved' ? '#F9A825' : '#BBB'}`,
                      opacity: v.status === 'pending' ? 0.85 : 1,
                    }}>
                      📅 {v.date_from.slice(8)}.{MM}–{v.date_to.slice(8)}.{MM} · {v.working_days} р.д.
                      {v.status === 'pending' && ' · не подтверждено'}
                      {v.status === 'rejected' && ' · отклонено'}
                    </span>
                    <button onClick={() => remove(route.id)} style={{ background: 'none', border: 'none', color: 'var(--text-light)', fontSize: 16, cursor: 'pointer' }}>✕</button>
                  </>
                )}
              </div>
            </div>

            {v && v.status === 'pending' && v.prev_date_from && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, background: '#F7F7F7', padding: '6px 10px', borderRadius: 6 }}>
                Было: {v.prev_date_from.slice(8)}.{MM}–{v.prev_date_to.slice(8)}.{MM} → Стало: {v.date_from.slice(8)}.{MM}–{v.date_to.slice(8)}.{MM}
              </div>
            )}
            {v && v.status === 'pending' && !v.prev_date_from && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, background: '#F7F7F7', padding: '6px 10px', borderRadius: 6 }}>
                Новый запрос — ранее отпуск не был запланирован
              </div>
            )}

            {onApprove && v && v.status === 'pending' && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <Btn size="sm" color="success" onClick={() => onApprove(v.id)}>✓ Подтвердить</Btn>
                <Btn size="sm" color="danger" onClick={() => onReject(v.id)}>✕ Отклонить</Btn>
              </div>
            )}

            <button onClick={() => isOpen ? setPickerOpen(null) : openPicker(route.id)} style={{ display: 'inline-flex', gap: 6, padding: '6px 12px', borderRadius: 6, border: '1.5px dashed #F9A825', background: '#FFFDE7', color: '#7D5A00', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              📅 {v ? 'Изменить даты' : 'Выбрать даты'}
            </button>

            {isOpen && (
              <div style={{ marginTop: 10, background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)', padding: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 8 }}>
                  {!selecting?.from ? 'Выберите первый день' : !selecting?.to ? `Начало: ${selecting.from} ${MON}` : `${selecting.from}–${selecting.to} ${MON} · ${workDays(selecting.from, selecting.to)} р.д.`}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
                  {DOW_HDRS.map((d, i) => <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: i>=5?'#E53E3E':'var(--text-muted)' }}>{d}</div>)}
                  {Array.from({ length: OFFSET }).map((_, i) => <div key={`e${i}`} />)}
                  {Array.from({ length: DIM }, (_, i) => i+1).map(day => {
                    const isWe = isWeekendDay(day);
                    const isStart = day === selecting?.from, isEnd = day === selecting?.to;
                    const inRange = selecting?.from && selecting?.to && day > selecting.from && day < selecting.to;
                    return (
                      <div key={day} onClick={() => dayClick(day)} style={{
                        textAlign: 'center', fontSize: 12, padding: '6px 2px', borderRadius: 5, cursor: isWe?'default':'pointer',
                        color: isWe?'#CBD5E0':'var(--text)',
                        background: (isStart||isEnd) ? '#F9A825' : inRange ? '#FFF9C4' : 'transparent',
                        fontWeight: (isStart||isEnd)?700:500,
                      }}>{day}</div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <Btn size="md" color="ghost" onClick={() => setPickerOpen(null)} style={{ flex: 1 }}>Отмена</Btn>
                  <Btn size="md" onClick={save} disabled={!selecting?.from || !selecting?.to || saving} style={{ flex: 1, background: '#F9A825' }}>
                    {saving ? '...' : 'Сохранить'}
                  </Btn>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </Card>
    </>
  );
}
